import { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, Button, Modal } from '../components/ui';
import Avatar from '../components/Avatar';

const Feed = () => {
  const { user } = useContext(AuthContext);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPost, setNewPost] = useState('');
  const [expandedComments, setExpandedComments] = useState({});
  const [postComments, setPostComments] = useState({});
  const [newComments, setNewComments] = useState({});
  const [loadingComments, setLoadingComments] = useState({});
  const [showShareModal, setShowShareModal] = useState(false);
  const [sharePostId, setSharePostId] = useState(null);
  const [showFlagModal, setShowFlagModal] = useState(false);
  const [flagPostId, setFlagPostId] = useState(null);
  const [flagReason, setFlagReason] = useState('');
  const [flagDescription, setFlagDescription] = useState('');
  
  // Reply functionality state
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyTexts, setReplyTexts] = useState({});
  
  // File upload state
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [fileUploadProgress, setFileUploadProgress] = useState(0);

  // Text formatting state
  const [showFormattingToolbar, setShowFormattingToolbar] = useState(false);
  const textareaRef = useRef(null);

  // Feed Configuration state
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [feedConfig, setFeedConfig] = useState({
    sortBy: 'newest', // newest, oldest, popular
    filterBy: 'all', // all, text, image, video, event
    displayMode: 'detailed', // compact, detailed
    autoRefresh: false,
    refreshInterval: 30000, // 30 seconds
    postsPerLoad: 20,
    hideUsers: [],
    hideKeywords: []
  });

  useEffect(() => {
    console.log('🔄 Feed component mounted');
    console.log('👤 Current user:', user);
    console.log('🎫 Token in localStorage:', !!localStorage.getItem('token'));
    
    // Load saved feed configuration
    loadFeedConfig();
    fetchPosts();
  }, []);

  // Auto-refresh effect
  useEffect(() => {
    let interval;
    if (feedConfig.autoRefresh && feedConfig.refreshInterval > 0) {
      interval = setInterval(() => {
        console.log('🔄 Auto-refreshing feed...');
        fetchPosts();
      }, feedConfig.refreshInterval);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [feedConfig.autoRefresh, feedConfig.refreshInterval]);

  const loadFeedConfig = () => {
    try {
      const savedConfig = localStorage.getItem('feedConfig');
      if (savedConfig) {
        const config = JSON.parse(savedConfig);
        setFeedConfig(prevConfig => ({ ...prevConfig, ...config }));
        console.log('⚙️ Loaded feed config:', config);
      }
    } catch (error) {
      console.error('❌ Error loading feed config:', error);
    }
  };

  const saveFeedConfig = (newConfig) => {
    try {
      localStorage.setItem('feedConfig', JSON.stringify(newConfig));
      console.log('💾 Saved feed config:', newConfig);
    } catch (error) {
      console.error('❌ Error saving feed config:', error);
    }
  };

  const fetchPosts = async () => {
    try {
      console.log('🔄 Fetching posts...');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      console.log('🔗 Backend URL:', backendUrl);
      const token = localStorage.getItem('token');
      console.log('🎫 Token available:', !!token);
      
      const response = await axios.get(`${backendUrl}/api/posts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('📰 Posts received:', response.data.length);
      
      // Apply filters and sorting
      let filteredPosts = applyFiltersAndSorting(response.data);
      setPosts(filteredPosts);
    } catch (error) {
      console.error('❌ Error fetching posts:', error);
      console.error('❌ Error details:', error.response?.data);
    } finally {
      setLoading(false);
    }
  };

  const applyFiltersAndSorting = (postsData) => {
    let filtered = [...postsData];

    // Apply type filter
    if (feedConfig.filterBy !== 'all') {
      filtered = filtered.filter(post => {
        switch (feedConfig.filterBy) {
          case 'text': return !post.attachments || post.attachments.length === 0;
          case 'image': return post.attachments && post.attachments.some(att => att.type?.startsWith('image/'));
          case 'video': return post.attachments && post.attachments.some(att => att.type?.startsWith('video/'));
          case 'event': return post.post_type === 'event';
          default: return true;
        }
      });
    }

    // Apply user filters
    if (feedConfig.hideUsers.length > 0) {
      filtered = filtered.filter(post => !feedConfig.hideUsers.includes(post.user?.id));
    }

    // Apply keyword filters
    if (feedConfig.hideKeywords.length > 0) {
      filtered = filtered.filter(post => {
        const content = (post.content || '').toLowerCase();
        return !feedConfig.hideKeywords.some(keyword => 
          content.includes(keyword.toLowerCase())
        );
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (feedConfig.sortBy) {
        case 'oldest':
          return new Date(a.created_at) - new Date(b.created_at);
        case 'popular':
          return (b.likes_count + b.comments_count) - (a.likes_count + a.comments_count);
        case 'newest':
        default:
          return new Date(b.created_at) - new Date(a.created_at);
      }
    });

    console.log(`🔧 Applied filters: ${postsData.length} → ${filtered.length} posts`);
    return filtered;
  };

  // Text formatting functions
  const formatText = (type) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = newPost.substring(start, end);
    
    let formattedText;
    
    switch (type) {
      case 'bold':
        formattedText = `**${selectedText}**`;
        break;
      case 'italic':
        formattedText = `*${selectedText}*`;
        break;
      case 'underline':
        formattedText = `__${selectedText}__`;
        break;
      default:
        return;
    }
    
    const newContent = newPost.substring(0, start) + formattedText + newPost.substring(end);
    setNewPost(newContent);
  };

  const insertSpecialText = (type) => {
    console.log('🔗 insertSpecialText called with type:', type);
    const textarea = textareaRef.current;
    if (!textarea) {
      console.log('❌ Textarea ref is null');
      return;
    }

    const start = textarea.selectionStart;
    let insertText;
    
    switch (type) {
      case 'hashtag':
        insertText = '#';
        break;
      case 'mention':
        insertText = '@';
        break;
      case 'link':
        insertText = '[Link Text](URL)';
        break;
      default:
        console.log('❌ Unknown type:', type);
        return;
    }
    
    console.log('✅ Inserting text:', insertText, 'at position:', start);
    const newContent = newPost.substring(0, start) + insertText + newPost.substring(start);
    setNewPost(newContent);
    console.log('✅ New post content:', newContent);
  };

  const renderFormattedText = (text) => {
    // Simple markdown-like formatting for display
    let formattedText = text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/__(.*?)__/g, '<u>$1</u>')
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" class="text-blue-600 hover:text-blue-800 underline">$1</a>')
      .replace(/(#\w+)/g, '<span class="text-blue-500 font-semibold">$1</span>')
      .replace(/(@\w+)/g, '<span class="text-green-500 font-semibold">$1</span>');
    
    return { __html: formattedText };
  };

  const createPost = async () => {
    if (!newPost.trim()) return;

    try {
      setUploadingFiles(true);
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      // Upload files first if any are selected
      let attachmentIds = [];
      if (selectedFiles.length > 0) {
        for (let i = 0; i < selectedFiles.length; i++) {
          const file = selectedFiles[i];
          const formData = new FormData();
          formData.append('file', file);
          formData.append('category', 'posts');
          
          setFileUploadProgress(Math.round(((i + 1) / selectedFiles.length) * 100));
          
          const uploadResponse = await axios.post(`${backendUrl}/api/files/upload`, formData, {
            headers: { 
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'multipart/form-data'
            }
          });
          
          if (uploadResponse.data.file) {
            attachmentIds.push(uploadResponse.data.file.id);
          }
        }
      }
      
      // Create post with attachments
      const response = await axios.post(`${backendUrl}/api/posts`, {
        content: newPost,
        post_type: 'text',
        attachments: attachmentIds
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // Add the new post to the beginning of the posts array
      const enrichedPost = {
        ...response.data,
        user: user
      };
      setPosts([enrichedPost, ...posts]);
      setNewPost('');
      setSelectedFiles([]);
      setFileUploadProgress(0);
      setShowFormattingToolbar(false);
      
    } catch (error) {
      console.error('Error creating post:', error);
      alert('Failed to create post. Please try again.');
    } finally {
      setUploadingFiles(false);
    }
  };

  // File upload functions
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const validFiles = [];
    
    for (const file of files) {
      // Validate file type
      const isImage = file.type.startsWith('image/');
      const isDocument = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/plain'
      ].includes(file.type);
      
      if (!isImage && !isDocument) {
        alert(`File "${file.name}" is not supported. Please upload images or documents only.`);
        continue;
      }
      
      // Validate file size (10MB for images, 25MB for documents)
      const maxSize = isImage ? 10 * 1024 * 1024 : 25 * 1024 * 1024;
      if (file.size > maxSize) {
        const maxMB = maxSize / (1024 * 1024);
        alert(`File "${file.name}" is too large. Maximum size is ${maxMB}MB.`);
        continue;
      }
      
      validFiles.push(file);
    }
    
    setSelectedFiles(prev => [...prev, ...validFiles]);
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const likePost = async (postId) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await axios.post(`${backendUrl}/api/posts/${postId}/like`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // Update the post likes count based on server response
      setPosts(posts.map(post => 
        post.id === postId 
          ? { 
              ...post, 
              likes_count: response.data.liked 
                ? post.likes_count + 1 
                : post.likes_count - 1,
              user_liked: response.data.liked
            }
          : post
      ));
    } catch (error) {
      console.error('Error liking post:', error);
    }
  };

  // Comment Functions
  const toggleComments = async (postId) => {
    console.log('💬 Toggle comments for post:', postId);
    setExpandedComments(prev => ({
      ...prev,
      [postId]: !prev[postId]
    }));

    // Fetch comments if not already loaded
    if (!postComments[postId] && !expandedComments[postId]) {
      await fetchComments(postId);
    }
  };

  const fetchComments = async (postId) => {
    console.log('📖 Fetching comments for post:', postId);
    setLoadingComments(prev => ({ ...prev, [postId]: true }));
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const token = localStorage.getItem('token');
      const response = await axios.get(`${backendUrl}/api/posts/${postId}/comments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('📖 Comments received:', response.data.length);
      setPostComments(prev => ({
        ...prev,
        [postId]: response.data
      }));
    } catch (error) {
      console.error('❌ Error fetching comments:', error);
    } finally {
      setLoadingComments(prev => ({ ...prev, [postId]: false }));
    }
  };

  const addComment = async (postId, content, parentCommentId = null) => {
    const commentContent = content || newComments[postId];
    if (!commentContent?.trim()) return;

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await axios.post(`${backendUrl}/api/posts/${postId}/comments`, {
        content: commentContent,
        parent_comment_id: parentCommentId
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      // Add user info to the comment
      const enrichedComment = {
        ...response.data,
        user: user
      };

      // Update comments list
      setPostComments(prev => ({
        ...prev,
        [postId]: [...(prev[postId] || []), enrichedComment]
      }));

      // Update post comments count
      setPosts(posts.map(post => 
        post.id === postId 
          ? { ...post, comments_count: post.comments_count + 1 }
          : post
      ));

      // Clear input
      if (parentCommentId) {
        // Clear reply input
        setReplyTexts(prev => ({ ...prev, [parentCommentId]: '' }));
        setReplyingTo(null);
      } else {
        // Clear main comment input
        setNewComments(prev => ({ ...prev, [postId]: '' }));
      }

      console.log(`✅ Added ${parentCommentId ? 'reply' : 'comment'} to post ${postId}`);
    } catch (error) {
      console.error('❌ Error adding comment:', error);
    }
  };

  const handleReply = (commentId) => {
    setReplyingTo(replyingTo === commentId ? null : commentId);
  };

  // Share Functions
  const handleShare = (postId) => {
    console.log('🔗 Share button clicked for post:', postId);
    setSharePostId(postId);
    setShowShareModal(true);
  };

  const shareToClipboard = async (postId) => {
    const post = posts.find(p => p.id === postId);
    const shareText = `Check out this post by ${post.user?.full_name}: "${post.content.substring(0, 100)}${post.content.length > 100 ? '...' : ''}"`;
    
    try {
      await navigator.clipboard.writeText(`${shareText}\n\n${window.location.origin}/posts/${postId}`);
      alert('Post link copied to clipboard!');
      setShowShareModal(false);
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = `${shareText}\n\n${window.location.origin}/posts/${postId}`;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert('Post link copied to clipboard!');
      setShowShareModal(false);
    }
  };

  const shareToSocial = (platform, postId) => {
    const post = posts.find(p => p.id === postId);
    const shareText = `Check out this post by ${post.user?.full_name}: "${post.content.substring(0, 100)}${post.content.length > 100 ? '...' : ''}"`;
    const shareUrl = `${window.location.origin}/posts/${postId}`;
    
    let url;
    switch(platform) {
      case 'twitter':
        url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
        break;
      case 'linkedin':
        url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
        break;
      case 'facebook':
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
        break;
      case 'whatsapp':
        url = `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(url, '_blank', 'width=600,height=400');
    setShowShareModal(false);
  };

  // Flag functionality
  const handleFlag = (postId) => {
    setFlagPostId(postId);
    setShowFlagModal(true);
    setFlagReason('');
    setFlagDescription('');
  };

  const submitFlag = async () => {
    if (!flagReason) {
      alert('Please select a reason for flagging this content');
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      await axios.post(`${backendUrl}/api/admin/flags`, {
        content_type: 'post',
        content_id: flagPostId,
        reason: flagReason,
        description: flagDescription
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      alert('Content flagged successfully. Thank you for helping keep our community safe.');
      setShowFlagModal(false);
      setFlagPostId(null);
      setFlagReason('');
      setFlagDescription('');
    } catch (error) {
      console.error('Error flagging content:', error);
      
      // Handle specific error cases
      if (error.response?.status === 400) {
        const errorMessage = error.response.data?.detail || 'Invalid request';
        if (errorMessage.includes('already flagged')) {
          alert('You have already flagged this content.');
        } else {
          alert(`Error: ${errorMessage}`);
        }
      } else if (error.response?.status === 404) {
        alert('Content not found. It may have been deleted.');
      } else {
        alert('Failed to flag content. Please try again.');
      }
    }
  };

  // Feed Configuration Handlers
  const handleConfigChange = (key, value) => {
    const newConfig = { ...feedConfig, [key]: value };
    setFeedConfig(newConfig);
    saveFeedConfig(newConfig);
    
    // Re-apply filters if posts are already loaded
    if (posts.length > 0 && (key === 'sortBy' || key === 'filterBy' || key === 'hideUsers' || key === 'hideKeywords')) {
      const filteredPosts = applyFiltersAndSorting(posts);
      setPosts(filteredPosts);
    }
  };

  const addHideUser = (userId, userName) => {
    if (!feedConfig.hideUsers.includes(userId)) {
      const newHideUsers = [...feedConfig.hideUsers, userId];
      handleConfigChange('hideUsers', newHideUsers);
      console.log(`🙈 Added ${userName} to hidden users`);
    }
  };

  const removeHideUser = (userId) => {
    const newHideUsers = feedConfig.hideUsers.filter(id => id !== userId);
    handleConfigChange('hideUsers', newHideUsers);
  };

  const addHideKeyword = (keyword) => {
    if (keyword.trim() && !feedConfig.hideKeywords.includes(keyword.trim().toLowerCase())) {
      const newKeywords = [...feedConfig.hideKeywords, keyword.trim().toLowerCase()];
      handleConfigChange('hideKeywords', newKeywords);
    }
  };

  const removeHideKeyword = (keyword) => {
    const newKeywords = feedConfig.hideKeywords.filter(k => k !== keyword);
    handleConfigChange('hideKeywords', newKeywords);
  };

  const resetConfiguration = () => {
    const defaultConfig = {
      sortBy: 'newest',
      filterBy: 'all',
      displayMode: 'detailed',
      autoRefresh: false,
      refreshInterval: 30000,
      postsPerLoad: 20,
      hideUsers: [],
      hideKeywords: []
    };
    setFeedConfig(defaultConfig);
    saveFeedConfig(defaultConfig);
    fetchPosts(); // Refresh with default settings
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  // Configuration Modal Component
  const ConfigurationModal = () => (
    <Modal
      isOpen={showConfigModal}
      onClose={() => setShowConfigModal(false)}
      title="⚙️ Feed Configuration"
      showCloseButton={true}
    >
      <div className="space-y-6 max-h-[70vh] overflow-y-auto">
        {/* Sort Options */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">📊 Sort Posts By</h3>
          <div className="space-y-2">
            {[
              { value: 'newest', label: '🕐 Newest First', desc: 'Show latest posts first' },
              { value: 'oldest', label: '🕑 Oldest First', desc: 'Show older posts first' },
              { value: 'popular', label: '🔥 Most Popular', desc: 'Sort by likes and comments' }
            ].map(option => (
              <label key={option.value} className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-gray-50 cursor-pointer">
                <input
                  type="radio"
                  name="sortBy"
                  value={option.value}
                  checked={feedConfig.sortBy === option.value}
                  onChange={(e) => handleConfigChange('sortBy', e.target.value)}
                  className="w-4 h-4 text-blue-600"
                />
                <div>
                  <div className="font-medium text-gray-900">{option.label}</div>
                  <div className="text-sm text-gray-500">{option.desc}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Filter Options */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">🔍 Filter Posts By Type</h3>
          <div className="space-y-2">
            {[
              { value: 'all', label: '📰 All Posts', desc: 'Show all post types' },
              { value: 'text', label: '📝 Text Only', desc: 'Show text posts without attachments' },
              { value: 'image', label: '🖼️ With Images', desc: 'Show posts with images' },
              { value: 'video', label: '🎥 With Videos', desc: 'Show posts with videos' },
              { value: 'event', label: '📅 Events', desc: 'Show event posts only' }
            ].map(option => (
              <label key={option.value} className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-gray-50 cursor-pointer">
                <input
                  type="radio"
                  name="filterBy"
                  value={option.value}
                  checked={feedConfig.filterBy === option.value}
                  onChange={(e) => handleConfigChange('filterBy', e.target.value)}
                  className="w-4 h-4 text-blue-600"
                />
                <div>
                  <div className="font-medium text-gray-900">{option.label}</div>
                  <div className="text-sm text-gray-500">{option.desc}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Display Mode */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">👁️ Display Mode</h3>
          <div className="space-y-2">
            {[
              { value: 'detailed', label: '📖 Detailed View', desc: 'Full posts with all content visible' },
              { value: 'compact', label: '📋 Compact View', desc: 'Condensed posts for quick browsing' }
            ].map(option => (
              <label key={option.value} className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-gray-50 cursor-pointer">
                <input
                  type="radio"
                  name="displayMode"
                  value={option.value}
                  checked={feedConfig.displayMode === option.value}
                  onChange={(e) => handleConfigChange('displayMode', e.target.value)}
                  className="w-4 h-4 text-blue-600"
                />
                <div>
                  <div className="font-medium text-gray-900">{option.label}</div>
                  <div className="text-sm text-gray-500">{option.desc}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Auto Refresh */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">🔄 Auto Refresh</h3>
          <div className="space-y-4">
            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={feedConfig.autoRefresh}
                onChange={(e) => handleConfigChange('autoRefresh', e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded"
              />
              <span className="font-medium text-gray-900">Enable auto-refresh</span>
            </label>
            
            {feedConfig.autoRefresh && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Refresh Interval (seconds)
                </label>
                <select
                  value={feedConfig.refreshInterval / 1000}
                  onChange={(e) => handleConfigChange('refreshInterval', parseInt(e.target.value) * 1000)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="15">15 seconds</option>
                  <option value="30">30 seconds</option>
                  <option value="60">1 minute</option>
                  <option value="300">5 minutes</option>
                  <option value="600">10 minutes</option>
                </select>
              </div>
            )}
          </div>
        </div>

        {/* Hidden Keywords */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">🙈 Hidden Keywords</h3>
          <div className="space-y-3">
            <div className="flex space-x-2">
              <input
                type="text"
                placeholder="Enter keyword to hide..."
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    addHideKeyword(e.target.value);
                    e.target.value = '';
                  }
                }}
              />
              <Button
                onClick={() => {
                  const input = document.querySelector('input[placeholder="Enter keyword to hide..."]');
                  if (input.value.trim()) {
                    addHideKeyword(input.value);
                    input.value = '';
                  }
                }}
                size="sm"
              >
                Add
              </Button>
            </div>
            
            {feedConfig.hideKeywords.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {feedConfig.hideKeywords.map((keyword, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-red-100 text-red-800"
                  >
                    {keyword}
                    <button
                      onClick={() => removeHideKeyword(keyword)}
                      className="ml-2 text-red-600 hover:text-red-800"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between pt-4 border-t">
          <Button
            onClick={resetConfiguration}
            variant="outline"
            className="text-red-600 hover:bg-red-50"
          >
            🔄 Reset to Default
          </Button>
          <div className="space-x-2">
            <Button
              onClick={() => setShowConfigModal(false)}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                fetchPosts(); // Refresh with current settings
                setShowConfigModal(false);
              }}
            >
              ✅ Apply & Close
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-300 rounded-lg w-1/3 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded-lg w-1/4"></div>
                </div>
              </div>
              <div className="space-y-3">
                <div className="h-4 bg-gray-300 rounded-lg"></div>
                <div className="h-4 bg-gray-300 rounded-lg w-4/5"></div>
                <div className="h-4 bg-gray-300 rounded-lg w-3/4"></div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Create Post Section - Standardized with Group Feed */}
        <Card className="mb-6" padding="lg">
          <div className="space-y-4">
            <div className="flex items-center space-x-4">
              <Avatar user={user} size="medium" />
              <div className="flex-1">
                <textarea
                  ref={textareaRef}
                  value={newPost}
                  onChange={(e) => setNewPost(e.target.value)}
                  onFocus={() => setShowFormattingToolbar(true)}
                  placeholder="Share something with your community..."
                  className="w-full border-none resize-none text-gray-900 placeholder-gray-400 focus:outline-none text-lg"
                  rows="3"
                />
              </div>
            </div>

            {/* Text Formatting Toolbar */}
            {showFormattingToolbar && (
              <div className="flex items-center space-x-2 px-4 py-2 bg-gray-50 rounded-lg">
                <button onClick={() => formatText('bold')} className="p-2 text-gray-600 hover:text-gray-900 hover:bg-white rounded" title="Bold">
                  <strong>B</strong>
                </button>
                <button onClick={() => formatText('italic')} className="p-2 text-gray-600 hover:text-gray-900 hover:bg-white rounded" title="Italic">
                  <em>I</em>
                </button>
                <button onClick={() => formatText('underline')} className="p-2 text-gray-600 hover:text-gray-900 hover:bg-white rounded" title="Underline">
                  <u>U</u>
                </button>
                <div className="w-px h-6 bg-gray-300"></div>
                <button onClick={() => insertSpecialText('hashtag')} className="p-2 text-gray-600 hover:text-gray-900 hover:bg-white rounded" title="Hashtag">
                  #️⃣
                </button>
                <button onClick={() => insertSpecialText('mention')} className="p-2 text-gray-600 hover:text-gray-900 hover:bg-white rounded" title="Mention">
                  @
                </button>
                <button onClick={() => insertSpecialText('link')} className="p-2 text-gray-600 hover:text-gray-900 hover:bg-white rounded" title="Link">
                  🔗
                </button>
              </div>
            )}

            {/* File Upload */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <input
                  type="file"
                  id="file-upload"
                  multiple
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <label 
                  htmlFor="file-upload"
                  className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-teal-600 hover:bg-teal-50 cursor-pointer transition-all duration-300 rounded-xl border border-gray-200 hover:border-teal-300"
                >
                  <span className="text-xl">📎</span>
                  <span className="font-medium">Attach Files</span>
                </label>
                <label 
                  htmlFor="file-upload"
                  className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 cursor-pointer transition-all duration-300 rounded-xl border border-gray-200 hover:border-purple-300"
                >
                  <span className="text-xl">📷</span>
                  <span className="font-medium">Photos</span>
                </label>
                {uploadingFiles && (
                  <div className="text-sm text-gray-600">
                    Uploading... {Math.round(fileUploadProgress)}%
                  </div>
                )}
              </div>
              <Button
                onClick={createPost}
                disabled={(!newPost.trim() && selectedFiles.length === 0) || uploadingFiles}
                className="bg-gradient-to-r from-teal-500 to-blue-500 hover:from-teal-600 hover:to-blue-600 text-white px-6 py-2"
              >
                Share Post
              </Button>
            </div>

            {/* Selected Files Preview */}
            {selectedFiles.length > 0 && (
              <div className="border border-gray-200 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-700 mb-3">Selected Files:</p>
                <div className="space-y-2">
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <div className="flex items-center space-x-3">
                        <span className="text-2xl">
                          {file.type.startsWith('image/') ? '🖼️' : 
                           file.type === 'application/pdf' ? '📄' : 
                           file.type.includes('word') ? '📝' : 
                           file.type.includes('excel') ? '📊' : '📄'}
                        </span>
                        <div>
                          <p className="font-medium text-gray-900">{file.name}</p>
                          <p className="text-sm text-gray-500">{formatFileSize(file.size)}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => removeFile(index)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-lg transition-colors"
                      >
                        ❌
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Posts Feed */}
        <div className="space-y-8">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin text-4xl mb-4">⏳</div>
              <p className="text-lg text-gray-600">Loading amazing posts...</p>
            </div>
          ) : posts.length === 0 ? (
            <Card className="text-center py-16">
              <div className="text-6xl mb-6">🎯</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">No posts yet</h3>
              <p className="text-gray-500 text-lg">Be the first to share something amazing with the community!</p>
            </Card>
          ) : (
            <>
              <div className="flex items-center justify-between mb-6">
                <div className="text-sm font-medium text-gray-600 bg-white px-4 py-2 rounded-full shadow-sm border border-gray-200">
                  <span className="text-teal-600">📰</span> Showing {posts.length} posts
                </div>
                <div className="flex items-center space-x-2">
                  <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-colors shadow-sm border border-gray-200">
                    <span>🔄</span>
                  </button>
                  <button 
                    onClick={() => setShowConfigModal(true)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-colors shadow-sm border border-gray-200"
                    title="Configure Feed Settings"
                  >
                    <span>⚙️</span>
                  </button>
                </div>
              </div>
              
              {posts.map((post) => {
                console.log('🎨 Rendering post:', post.id, 'with', post.comments_count, 'comments');
                return (
                  <Card 
                    key={post.id} 
                    className="overflow-hidden hover:shadow-xl transition-all duration-300 border-0 shadow-md"
                    padding="none"
                  >
                    {/* Post Header */}
                    <div className="p-6">
                      <div className="flex items-center space-x-4 mb-6">
                        <div className="relative">
                          <Avatar 
                            user={post.user_id === user?.id ? user : post.user} 
                            size="large" 
                          />
                          {post.user?.is_verified && (
                            <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs shadow-lg">
                              ✓
                            </div>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <h3 className="font-bold text-gray-900 text-lg">{post.user?.full_name || 'Unknown User'}</h3>
                          </div>
                          <div className="flex items-center space-x-2 text-sm text-gray-500">
                            <span className="font-medium">{formatTimeAgo(post.created_at)}</span>
                            <span>•</span>
                            <span className="bg-gray-100 px-2 py-1 rounded-full text-xs font-medium">
                              {post.user?.company_name || 'Individual'}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                            <span>⋯</span>
                          </button>
                        </div>
                      </div>

                      {/* Post Content */}
                      <div className="mb-6">
                        <div 
                          className="text-gray-900 text-lg leading-relaxed whitespace-pre-wrap"
                          dangerouslySetInnerHTML={renderFormattedText(post.content)}
                        />
                        
                        {/* Hashtags */}
                        {post.hashtags && post.hashtags.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-4">
                            {post.hashtags.map((tag, index) => (
                              <span 
                                key={index} 
                                className="bg-blue-50 text-blue-600 px-3 py-1 rounded-full text-sm font-medium hover:bg-blue-100 cursor-pointer transition-colors"
                              >
                                #{tag}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* File Attachments */}
                        {post.attachments && post.attachments.length > 0 && (
                          <div className="mt-6">
                            <div className="grid gap-3">
                              {post.attachments.map((attachmentId, index) => (
                                <div key={index} className="border border-gray-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow">
                                  <FileAttachmentDisplay attachmentId={attachmentId} />
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Post Actions */}
                      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                        <div className="flex items-center space-x-6">
                          <button
                            onClick={() => likePost(post.id)}
                            className={`flex items-center space-x-2 px-3 py-2 rounded-full transition-all duration-300 ${
                              post.user_liked 
                                ? 'text-red-500 bg-red-50 hover:bg-red-100' 
                                : 'text-gray-600 hover:text-red-500 hover:bg-red-50'
                            }`}
                          >
                            <span className="text-lg">{post.user_liked ? '❤️' : '🤍'}</span>
                            <span className="font-medium">{post.likes_count}</span>
                          </button>
                          <button 
                            onClick={() => toggleComments(post.id)}
                            className="flex items-center space-x-2 px-3 py-2 rounded-full text-gray-600 hover:text-blue-500 hover:bg-blue-50 transition-all duration-300"
                          >
                            <span className="text-lg">💬</span>
                            <span className="font-medium">{post.comments_count}</span>
                          </button>
                          <button 
                            onClick={() => handleShare(post.id)}
                            className="flex items-center space-x-2 px-3 py-2 rounded-full text-gray-600 hover:text-green-500 hover:bg-green-50 transition-all duration-300"
                          >
                            <span className="text-lg">🔄</span>
                            <span className="font-medium">Share</span>
                          </button>
                          <button 
                            onClick={() => handleFlag(post.id)}
                            className="flex items-center space-x-2 px-3 py-2 rounded-full text-gray-600 hover:text-yellow-500 hover:bg-yellow-50 transition-all duration-300"
                          >
                            <span className="text-lg">🚩</span>
                            <span className="font-medium">Flag</span>
                          </button>
                        </div>
                        
                        {post.group_id && (
                          <div className="bg-gray-100 px-3 py-1 rounded-full text-sm text-gray-600 font-medium">
                            📝 Group post
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Comments Section */}
                    {expandedComments[post.id] && (
                      <div className="border-t border-gray-100 bg-gray-50">
                        {/* Add Comment */}
                        <div className="p-6 border-b border-gray-200 bg-white">
                          <div className="flex items-start space-x-4">
                            <Avatar user={user} size="small" />
                            <div className="flex-1 flex space-x-3">
                              <input
                                type="text"
                                placeholder="Write a thoughtful comment..."
                                value={newComments[post.id] || ''}
                                onChange={(e) => setNewComments(prev => ({
                                  ...prev,
                                  [post.id]: e.target.value
                                }))}
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    addComment(post.id);
                                  }
                                }}
                                className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-2xl focus:outline-none focus:ring-4 focus:ring-teal-500/20 focus:border-teal-500 transition-all duration-300"
                              />
                              <Button
                                onClick={() => addComment(post.id)}
                                disabled={!newComments[post.id]?.trim()}
                                variant="primary"
                                className="bg-gradient-to-r from-teal-600 to-blue-600 hover:from-teal-700 hover:to-blue-700 px-6"
                              >
                                <span className="mr-1">💬</span>
                                Post
                              </Button>
                            </div>
                          </div>
                        </div>

                        {/* Comments List */}
                        <div className="p-6">
                          {loadingComments[post.id] ? (
                            <div className="space-y-4">
                              {[...Array(2)].map((_, i) => (
                                <div key={i} className="flex items-start space-x-4 animate-pulse">
                                  <div className="w-10 h-10 bg-gray-300 rounded-full"></div>
                                  <div className="flex-1">
                                    <div className="h-4 bg-gray-300 rounded-lg w-1/4 mb-2"></div>
                                    <div className="h-16 bg-gray-300 rounded-2xl w-full"></div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : postComments[post.id]?.length > 0 ? (
                            <div className="space-y-4">
                              {postComments[post.id]
                                .filter(comment => !comment.parent_comment_id) // Top-level comments only
                                .map((comment) => (
                                <div key={comment.id}>
                                  <div className="flex items-start space-x-4">
                                    <Avatar 
                                      user={comment.user_id === user?.id ? user : comment.user} 
                                      size="small" 
                                    />
                                    <div className="flex-1">
                                      <div className="bg-white rounded-2xl px-4 py-3 shadow-sm border border-gray-100">
                                        <div className="flex items-center space-x-2 mb-2">
                                          <span className="font-semibold text-gray-900">
                                            {comment.user?.full_name || 'Unknown User'}
                                          </span>
                                          <span className="text-xs text-gray-400">
                                            {formatTimeAgo(comment.created_at)}
                                          </span>
                                        </div>
                                        <p className="text-gray-800 leading-relaxed">{comment.content}</p>
                                      </div>
                                      <div className="flex items-center space-x-6 mt-2 ml-4">
                                        <button className="text-sm text-gray-500 hover:text-red-500 transition-colors">
                                          <span className="mr-1">❤️</span>Like
                                        </button>
                                        <button 
                                          onClick={() => handleReply(comment.id)}
                                          className="text-sm text-gray-500 hover:text-blue-500 transition-colors"
                                        >
                                          <span className="mr-1">💬</span>Reply
                                        </button>
                                        <button className="text-sm text-gray-500 hover:text-yellow-500 transition-colors">
                                          <span className="mr-1">🚩</span>Flag
                                        </button>
                                      </div>
                                      
                                      {/* Reply Input for this comment */}
                                      {replyingTo === comment.id && (
                                        <div className="mt-3 ml-4">
                                          <div className="flex items-start space-x-3">
                                            <Avatar user={user} size="small" className="w-6 h-6" />
                                            <div className="flex-1">
                                              <input
                                                type="text"
                                                placeholder={`Reply to ${comment.user?.full_name}...`}
                                                value={replyTexts[comment.id] || ''}
                                                onChange={(e) => setReplyTexts(prev => ({ ...prev, [comment.id]: e.target.value }))}
                                                className="w-full bg-white border border-blue-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                onKeyPress={(e) => {
                                                  if (e.key === 'Enter' && e.target.value.trim()) {
                                                    addComment(post.id, e.target.value, comment.id);
                                                  }
                                                }}
                                              />
                                              <div className="flex items-center space-x-2 mt-2">
                                                <button
                                                  onClick={() => {
                                                    if (replyTexts[comment.id]?.trim()) {
                                                      addComment(post.id, replyTexts[comment.id], comment.id);
                                                    }
                                                  }}
                                                  disabled={!replyTexts[comment.id]?.trim()}
                                                  className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                  Reply
                                                </button>
                                                <button
                                                  onClick={() => setReplyingTo(null)}
                                                  className="text-xs text-gray-500 px-3 py-1 rounded hover:bg-gray-100"
                                                >
                                                  Cancel
                                                </button>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  
                                  {/* Nested Replies */}
                                  {postComments[post.id]
                                    .filter(reply => reply.parent_comment_id === comment.id)
                                    .map((reply) => (
                                    <div key={reply.id} className="flex items-start space-x-3 ml-8 mt-3">
                                      <Avatar 
                                        user={reply.user_id === user?.id ? user : reply.user} 
                                        size="small" 
                                        className="w-6 h-6" 
                                      />
                                      <div className="flex-1">
                                        <div className="bg-white rounded-lg p-3 border-l-2 border-blue-200">
                                          <div className="flex items-center space-x-2 mb-1">
                                            <span className="font-medium text-gray-900 text-sm">{reply.user?.full_name || 'Unknown User'}</span>
                                            <span className="text-xs text-gray-500">{formatTimeAgo(reply.created_at)}</span>
                                          </div>
                                          <p className="text-gray-700 text-sm">{reply.content}</p>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-12">
                              <div className="text-4xl mb-4">💭</div>
                              <p className="text-gray-500 text-lg mb-2">No comments yet</p>
                              <p className="text-gray-400">Be the first to share your thoughts!</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </Card>
                );
              })}
            </>
          )}
        </div>

        {/* Share Modal */}
        {showShareModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Share Post</h2>
                  <button
                    onClick={() => setShowShareModal(false)}
                    className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    ❌
                  </button>
                </div>
                
                <div className="space-y-3">
                  {/* Copy Link */}
                  <button
                    onClick={() => shareToClipboard(sharePostId)}
                    className="w-full flex items-center space-x-4 p-4 border border-gray-200 rounded-xl hover:bg-blue-50 hover:border-blue-300 transition-all duration-300 group"
                  >
                    <span className="text-3xl group-hover:scale-110 transition-transform">🔗</span>
                    <div className="text-left">
                      <div className="font-semibold text-gray-900">Copy Link</div>
                      <div className="text-sm text-gray-500">Copy link to clipboard</div>
                    </div>
                  </button>

                  {/* Social Media Sharing */}
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => shareToSocial('twitter', sharePostId)}
                      className="flex items-center space-x-3 p-4 border border-gray-200 rounded-xl hover:bg-blue-50 hover:border-blue-300 transition-all duration-300 group"
                    >
                      <span className="text-2xl group-hover:scale-110 transition-transform">🐦</span>
                      <span className="font-semibold text-gray-900">Twitter</span>
                    </button>
                    
                    <button
                      onClick={() => shareToSocial('linkedin', sharePostId)}
                      className="flex items-center space-x-3 p-4 border border-gray-200 rounded-xl hover:bg-blue-50 hover:border-blue-300 transition-all duration-300 group"
                    >
                      <span className="text-2xl group-hover:scale-110 transition-transform">💼</span>
                      <span className="font-semibold text-gray-900">LinkedIn</span>
                    </button>
                    
                    <button
                      onClick={() => shareToSocial('facebook', sharePostId)}
                      className="flex items-center space-x-3 p-4 border border-gray-200 rounded-xl hover:bg-blue-50 hover:border-blue-300 transition-all duration-300 group"
                    >
                      <span className="text-2xl group-hover:scale-110 transition-transform">📘</span>
                      <span className="font-semibold text-gray-900">Facebook</span>
                    </button>
                    
                    <button
                      onClick={() => shareToSocial('whatsapp', sharePostId)}
                      className="flex items-center space-x-3 p-4 border border-gray-200 rounded-xl hover:bg-green-50 hover:border-green-300 transition-all duration-300 group"
                    >
                      <span className="text-2xl group-hover:scale-110 transition-transform">💬</span>
                      <span className="font-semibold text-gray-900">WhatsApp</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Flag Modal */}
        {showFlagModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Flag Content</h2>
                  <button
                    onClick={() => setShowFlagModal(false)}
                    className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    ❌
                  </button>
                </div>
                
                <div className="space-y-6">
                  {/* Reason Selection */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      Reason for flagging
                    </label>
                    <select
                      value={flagReason}
                      onChange={(e) => setFlagReason(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-red-500/20 focus:border-red-500 transition-all duration-300"
                    >
                      <option value="">Select a reason</option>
                      <option value="spam">🚫 Spam</option>
                      <option value="inappropriate">⚠️ Inappropriate Content</option>
                      <option value="harassment">😠 Harassment</option>
                      <option value="hate_speech">💢 Hate Speech</option>
                      <option value="fake_news">❌ False Information</option>
                      <option value="copyright">©️ Copyright Violation</option>
                      <option value="other">🔍 Other</option>
                    </select>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      Additional details (optional)
                    </label>
                    <textarea
                      value={flagDescription}
                      onChange={(e) => setFlagDescription(e.target.value)}
                      placeholder="Please provide more details about why you're flagging this content..."
                      rows={4}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-red-500/20 focus:border-red-500 transition-all duration-300 resize-none"
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex space-x-3 pt-4">
                    <Button
                      onClick={() => setShowFlagModal(false)}
                      variant="ghost"
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={submitFlag}
                      variant="danger"
                      className="flex-1"
                    >
                      Submit Flag
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Configuration Modal */}
        <ConfigurationModal />
      </div>
    </div>
  );
};

// File Attachment Display Component
const FileAttachmentDisplay = ({ attachmentId }) => {
  const [fileInfo, setFileInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchFileInfo();
  }, [attachmentId]);

  const fetchFileInfo = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await axios.get(`${backendUrl}/api/files/${attachmentId}/info`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setFileInfo(response.data);
    } catch (error) {
      console.error('Error fetching file info:', error);
      setError('Failed to load file');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (fileInfo) {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const downloadUrl = `${backendUrl}/api/files/${attachmentId}`;
      window.open(downloadUrl, '_blank');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="flex items-center space-x-4 p-4 animate-pulse">
        <div className="w-16 h-16 bg-gray-300 rounded-xl"></div>
        <div className="flex-1">
          <div className="h-4 bg-gray-300 rounded-lg w-3/4 mb-2"></div>
          <div className="h-3 bg-gray-300 rounded-lg w-1/2"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center space-x-4 p-4 text-red-600">
        <span className="text-3xl">❌</span>
        <span className="font-medium">{error}</span>
      </div>
    );
  }

  if (!fileInfo) return null;

  const isImage = fileInfo.file_type === 'image';

  return (
    <div className="bg-white">
      {isImage ? (
        /* Image Display */
        <div className="relative group">
          <img 
            src={`${process.env.REACT_APP_BACKEND_URL || ''}/api/files/${attachmentId}`}
            alt={fileInfo.original_filename}
            className="w-full h-auto max-h-96 object-cover rounded-lg"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div className="hidden items-center justify-center p-8 bg-gray-100 rounded-lg">
            <span className="text-4xl mr-4">🖼️</span>
            <div className="text-center">
              <p className="font-medium text-gray-900">{fileInfo.original_filename}</p>
              <p className="text-sm text-gray-500">Image could not be displayed</p>
            </div>
          </div>
          {/* Image Overlay with Download */}
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100">
            <button
              onClick={handleDownload}
              className="bg-white text-gray-900 px-4 py-2 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            >
              <span className="mr-2">📥</span>
              Download
            </button>
          </div>
        </div>
      ) : (
        /* File Display */
        <div 
          onClick={handleDownload}
          className="flex items-center p-4 hover:bg-gray-50 cursor-pointer transition-colors border rounded-lg"
        >
          <div className="text-4xl mr-4">
            {fileInfo.mime_type === 'application/pdf' ? '📄' : 
             fileInfo.mime_type?.includes('word') ? '📝' : 
             fileInfo.mime_type?.includes('excel') || fileInfo.mime_type?.includes('sheet') ? '📊' : 
             fileInfo.mime_type?.includes('powerpoint') || fileInfo.mime_type?.includes('presentation') ? '📊' : 
             fileInfo.mime_type?.startsWith('text/') ? '📄' : '📎'}
          </div>
          <div className="flex-1">
            <p className="font-medium text-gray-900 truncate">
              {fileInfo.original_filename}
            </p>
            <p className="text-sm text-gray-500">
              {formatFileSize(fileInfo.file_size)} • 
              {fileInfo.file_type?.split('/')[1]?.toUpperCase() || 'FILE'}
            </p>
          </div>
          <div className="text-gray-400 hover:text-gray-600">
            <span className="text-2xl">📥</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default Feed;